import { normalizePlaceTreeNames } from './placeTreeNames.js';

const text = value => String(value ?? '').trim();
const nameKey = value => text(value).toLowerCase();
const ROUTINE_SPATIAL_VERSION = 3;
export const createWorldDraftId = (prefix = 'world') =>
  window.crypto?.randomUUID?.() || `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const createWorldSaveProgress = () => ({
  sessionId: createWorldDraftId(),
  savedIds: {},
  currenciesSaved: false,
  relationshipsSaved: false,
  savedRelationshipIds: [],
  savedRelationshipScores: {},
  savedRelationshipSignatures: {},
  routinesSavedIds: {},
  routineSpatialVersion: ROUTINE_SPATIAL_VERSION,
  knowledgeSaved: false,
  knowledgeJobId: null,
});
export const getBeingRelationshipSignature = (relationships, draftId) => JSON.stringify(
  (relationships || []).filter(relation => relation.fromDraftId === draftId || relation.toDraftId === draftId)
    .map(({ fromDraftId, toDraftId, relateType, score, reason, knownByEntityIds, eventDraftIds, socialSeedIds, history }) =>
      ({ fromDraftId, toDraftId, relateType, score, reason, knownByEntityIds, eventDraftIds, socialSeedIds, history }))
);

export const getExistingWorldRelationships = draft => {
  const catalog = getWorldDraftCatalog(draft);
  const beingIds = new Map(catalog.filter(entry => entry.kind === 'Being').map(entry =>
    [nameKey(entry.entity.name || entry.entity.entityName), entry.draftId]));
  const rows = [...catalog.filter(entry => entry.kind === 'Being').flatMap(entry => [
    ...(entry.entity.residentRelationships || []).map(relation => ({ fromDraftId: entry.draftId,
      toDraftId: relation.targetDraftBeingId || beingIds.get(nameKey(relation.targetResidentName)), relateType: relation.relateType, score: relation.score ?? 50 })),
    ...(entry.entity.placeRelationships || []).map(relation => ({ fromDraftId: entry.draftId,
      toDraftId: relation.targetPlaceId, relateType: relation.relateType, score: relation.score ?? 50 })),
  ]), ...(draft.worldDevelopment?.relationships || [])].filter(relation => relation.toDraftId);
  return [...new Map(rows.map(r => [`${r.fromDraftId}|${r.toDraftId}|${r.relateType}`, r])).values()];
};
export const getWorldDraftCatalog = ({ draftPlace, draftChildSuggestions = [], draftInhabitantSuggestions = [],
  draftCommercialInventoryItems = [], draftNotableLoadoutItems = [], worldDevelopment = {} }) => {
  const places = draftPlace ? [
    ...[draftPlace, ...draftChildSuggestions].map(entity => ({ ...entity, isNearby: false })),
    ...(worldDevelopment.nearbyPlaces || []).map(entity => ({ ...entity, isNearby: true })),
  ].map((entity, index) => ({
    kind: 'Place', draftId: entity.draftPlaceId || (index ? `draft-place:${index}` : 'draft-place:root'), entity,
  })) : [];
  const beings = [...draftInhabitantSuggestions, ...(worldDevelopment.nearbyInhabitants || [])].map((entity, index) => ({
    kind: 'Being', draftId: entity.draftBeingId || entity.populationPlanSlotId || `draft-being:${index}`, entity,
  }));
  const items = [...draftCommercialInventoryItems, ...draftNotableLoadoutItems].map((entity, index) => ({
    kind: 'Item', draftId: entity.draftItemId || `draft-item:${entity.inventorySlotId || entity.notableItemSlotId || index}`, entity,
  }));
  return [...places, ...beings, ...items].map(entry => ({ ...entry, identity: entry.entity, entity: {
    ...entry.entity, ...(worldDevelopment.finalRecords?.[entry.draftId]?.record || {}),
    // The reviewed collection controls containment, never AI-returned flags.
    ...(entry.kind === 'Place' ? { isNearby: entry.entity.isNearby } : {}),
    // Currencies and assignments remain owned by the reviewed planning draft.
    ...(entry.entity.currencies ? { currencies: entry.entity.currencies } : {}),
    ...(entry.kind === 'Being' && worldDevelopment.socialOutlines?.[entry.draftId]?.outline ? {
      socialOutline: worldDevelopment.socialOutlines[entry.draftId].outline,
      lifeStage: worldDevelopment.socialOutlines[entry.draftId].outline.lifeStage,
    } : {}),
  } }));
};

export const getWorldSaveEntries = draft => [
  ...getWorldDraftCatalog(draft).flatMap(entry => {
    if (entry.kind !== 'Item') return [entry];
    // Provisions already store a quantity; other Item records represent physical copies.
    const quantity = entry.entity.quantity ?? 1;
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 1000) {
      throw new Error(`${entry.entity.name || 'Item'} needs a whole quantity between 1 and 1000.`);
    }
    const count = nameKey(entry.entity.itemType) === 'provision' ? 1 : quantity;
    return Array.from({ length: count }, (_, unit) => ({
      ...entry, logicalDraftId: entry.draftId,
      draftId: unit ? `${entry.draftId}:copy:${unit}` : entry.draftId,
    }));
  }),
  ...(draft.draftEvents || []).map((entity, index) => ({
    kind: 'Event', draftId: entity.draftEventId || `draft-event:${index}`, entity,
  })),
];

export const isWorldSaveComplete = (draft, progress) => {
  try {
    return Boolean(progress?.relationshipsSaved) && Boolean(progress?.knowledgeSaved) && getWorldSaveEntries(draft).every(entry => progress.savedIds[entry.draftId]) &&
      progress.routineSpatialVersion === ROUTINE_SPATIAL_VERSION && getWorldDraftCatalog(draft).filter(entry => entry.kind === 'Being')
        .every(entry => progress.routinesSavedIds?.[entry.draftId] === progress.savedIds[entry.draftId]) &&
      (draft.worldDevelopment?.relationships || []).every(relation => progress.savedRelationshipIds?.includes(relation.draftRelationshipId) &&
        (progress.savedRelationshipSignatures?.[relation.draftRelationshipId] ? progress.savedRelationshipSignatures[relation.draftRelationshipId] === JSON.stringify(relation) : relation.operation !== 'set_score'));
  } catch { return false; }
};

export const savePlaceTreeWorld = async ({ draft, progress, api, onProgress = () => {},
  onSaved = () => {}, onRoutineSaved = () => {}, onStatus = () => {}, shouldStop = () => false }) => {
  // Let the saving state paint before inspecting a large restored draft.
  await new Promise(resolve => setTimeout(resolve, 0));
  draft = normalizePlaceTreeNames(draft);
  const entries = getWorldSaveEntries(draft);
  const catalog = getWorldDraftCatalog(draft);
  const ids = { ...progress.savedIds };
  const external = new Set([draft.selectedParentPlaceId, draft.draftPlace?.parentIdEntity].filter(Boolean));
  const existingIds = new Set();
  const placeByName = new Map();
  const beingByName = new Map();
  const addName = (map, name, id) => {
    const key = nameKey(name);
    if (map.has(key) && map.get(key) !== id) throw new Error(`Ambiguous entity name: ${name}`);
    map.set(key, id);
  };
  for (const entry of catalog) {
    if (entry.kind === 'Place') addName(placeByName, entry.entity.name || entry.entity.entityName, entry.draftId);
    if (entry.kind === 'Being') addName(beingByName, entry.entity.name || entry.entity.entityName, entry.draftId);
  }
  const draftIds = new Set(entries.map(entry => entry.draftId));
  // Resolve every reference before making any writes.
  const links = [];
  const link = (from, to, relateType, score, change) => {
    if (!from || !to || !relateType || (!draftIds.has(from) && !external.has(from)) || !draftIds.has(to)) {
      throw new Error(`Unresolved ${relateType || 'entity'} relationship: ${from || '(missing)'} → ${to || '(missing)'}.`);
    }
    links.push({ from, to, relateType, ...(score !== undefined ? { score } : {}), ...(change || {}) });
  };
  const rootId = catalog.find(entry => entry.kind === 'Place')?.draftId;
  if (!rootId) throw new Error('Generate a Place draft before saving.');
  const parents = new Map();
  for (const entry of entries) {
    const { kind, draftId, entity } = entry;
    const existing = entity.savedPlaceId || entity.savedBeingId || entity.savedItemId || entity.savedEventId;
    if (existing) { ids[draftId] = existing; existingIds.add(draftId); }
    if (kind === 'Place') {
      const parent = draftId === rootId
        ? draft.draftPlace.parentIdEntity || draft.selectedParentPlaceId
        : entity.isNearby ? draft.draftPlace.parentIdEntity || draft.selectedParentPlaceId
        : entity.parentSuggestionName ? placeByName.get(nameKey(entity.parentSuggestionName)) : rootId;
      if (draftId !== rootId && !entity.isNearby && !parent) throw new Error(`Unknown parent for ${entity.name}.`);
      parents.set(draftId, parent);
      if (parent) link(parent, draftId, 'Composed Of');
      if (entity.isNearby) link(rootId, draftId, 'Near');
    }
    if (kind === 'Being') {
      if (entity.identityOnly) throw new Error(`Generate the full Being record for ${entity.name} before saving.`);
      const prepared = draft.worldDevelopment?.finalRecords?.[draftId];
      if (!ids[draftId] && prepared && prepared.eventSignature !== JSON.stringify(draft.draftEvents || [])) throw new Error(`Events changed since ${entity.name} was prepared. Prepare the Being again before saving.`);
      if (!ids[draftId] && prepared && (prepared.relationshipSignature || '[]') !== getBeingRelationshipSignature(draft.worldDevelopment?.relationships, draftId)) throw new Error(`Relationships changed since ${entity.name} was prepared. Prepare the Being again before saving.`);
      if (!ids[draftId] && prepared && (prepared.socialSignature || 'null') !== JSON.stringify(entity.socialOutline || null)) throw new Error(`Social outline changed since ${entity.name} was prepared. Prepare the Being again before saving.`);
      link(draftId, entity.memberOfPlaceId, 'Member Of');
      for (const relation of entity.placeRelationships || []) link(draftId, relation.targetPlaceId, relation.relateType, relation.score);
      for (const placeId of entity.socialOutline?.socialPlaceIds || []) link(draftId, placeId, 'Frequents');
      for (const relation of entity.residentRelationships || []) {
        link(draftId, relation.targetDraftBeingId || beingByName.get(nameKey(relation.targetResidentName)), relation.relateType, relation.score);
      }
    }
    if (kind === 'Item') {
      const owner = entity.ownerDraftBeingId || entity.commercialPlaceId;
      link(owner, draftId, 'Owns');
      link(owner, draftId, 'Contains');
    }
    if (kind === 'Event') {
      if (!text(entity.summary) || text(entity.summary).length > 128 || !text(entity.details)) throw new Error('Events need a summary up to 128 characters and nonempty details.');
      if (entity.placeDraftId) link(entity.placeDraftId, draftId, 'Occurred At');
      for (const participant of entity.participants || []) if (participant.relateType !== 'Heard') link(participant.entityDraftId, draftId, participant.relateType);
      for (const knower of entity.knownByEntityIds || []) {
        if (!catalog.some(entry => entry.kind === 'Being' && entry.draftId === knower)) throw new Error(`Unresolved initial event knower: ${knower}.`);
      }
    }
  }
  for (const relation of draft.worldDevelopment?.relationships || []) {
    if (!relation.draftRelationshipId || relation.fromDraftId === relation.toDraftId || !text(relation.reason) ||
      !Number.isInteger(relation.score) || relation.score < 0 || relation.score > 100 ||
      !Array.isArray(relation.eventDraftIds) || (!relation.eventDraftIds.length && !relation.socialSeedIds?.length) ||
      (relation.socialSeedIds || []).some(id => !catalog.some(entry => entry.kind === 'Being' && `social:${entry.draftId}` === id && entry.entity.socialOutline)) ||
      relation.eventDraftIds.some(id => !(draft.draftEvents || []).some(event => event.draftEventId === id))) {
      throw new Error('Generated relationships need valid endpoints, scores and supporting Events.');
    }
    link(relation.fromDraftId, relation.toDraftId, relation.relateType, relation.score, relation.operation === 'set_score' ? {
      operation: 'set_score', expectedScore: progress.savedRelationshipScores?.[relation.draftRelationshipId] ??
        (progress.savedRelationshipIds?.includes(relation.draftRelationshipId) ? relation.expectedScore : relation.history?.[0]?.score ?? relation.expectedScore), allowCreate: !progress.relationshipsSaved,
    } : null);
  }
  if (draftIds.size !== entries.length) throw new Error('Draft record ids must be unique.');
  const visited = new Set();
  const visit = (id, path = new Set()) => {
    if (visited.has(id)) return;
    if (path.has(id)) throw new Error('The sub-place hierarchy contains a cycle.');
    const parent = parents.get(id);
    if (parents.has(parent)) visit(parent, new Set([...path, id]));
    visited.add(id);
  };
  for (const id of parents.keys()) visit(id);
  let currenciesSaved = Boolean(progress.currenciesSaved);
  let relationshipsSaved = false;
  // Old browser checkpoints predate mapped targets and full map context.
  // Let the server validate/reuse or regenerate them under the current contract.
  const routinesSavedIds = progress.routineSpatialVersion === ROUTINE_SPATIAL_VERSION ? { ...progress.routinesSavedIds } : {};
  for (const relation of draft.worldDevelopment?.relationships || []) if (!progress.savedRelationshipIds?.includes(relation.draftRelationshipId) ||
    (progress.savedRelationshipSignatures?.[relation.draftRelationshipId] ? progress.savedRelationshipSignatures[relation.draftRelationshipId] !== JSON.stringify(relation) : relation.operation === 'set_score')) {
    delete routinesSavedIds[relation.fromDraftId]; delete routinesSavedIds[relation.toDraftId];
  }
  let knowledgeSaved = Boolean(progress.knowledgeSaved), knowledgeJobId = progress.knowledgeJobId;
  if (entries.some(entry => !progress.savedIds[entry.draftId]) ||
    catalog.some(entry => entry.kind === 'Being' && routinesSavedIds[entry.draftId] !== progress.savedIds[entry.draftId]) ||
    (draft.worldDevelopment?.relationships || []).some(relation => !progress.savedRelationshipIds?.includes(relation.draftRelationshipId))) {
    knowledgeSaved = false; knowledgeJobId = null;
  }
  const snapshot = (complete = relationshipsSaved) => ({ ...progress, savedIds: { ...ids }, currenciesSaved, relationshipsSaved: complete,
    knowledgeSaved, knowledgeJobId,
    savedRelationshipScores: complete ? Object.fromEntries((draft.worldDevelopment?.relationships || []).map(r => [r.draftRelationshipId, r.score])) : (progress.savedRelationshipScores || {}),
    savedRelationshipSignatures: complete ? Object.fromEntries((draft.worldDevelopment?.relationships || []).map(r => [r.draftRelationshipId, JSON.stringify(r)])) : (progress.savedRelationshipSignatures || {}),
    routinesSavedIds: { ...routinesSavedIds },
    routineSpatialVersion: ROUTINE_SPATIAL_VERSION,
    savedRelationshipIds: complete ? (draft.worldDevelopment?.relationships || []).map(relation => relation.draftRelationshipId) : (progress.savedRelationshipIds || []) });
  onProgress(snapshot());
  const stopped = () => ({ progress: snapshot(), stopped: true });
  let savedCount = 0;
  const save = async entry => {
    const { kind, draftId, entity } = entry;
    // Acknowledged records are already represented in the restored graph.
    // Replaying callbacks rebuilt that graph thousands of times on Retry.
    if (progress.savedIds[draftId] && progress.savedIds[draftId] === ids[draftId]) {
      // A small progress checkpoint may be newer than the full display graph.
      // Let the UI restore missing nodes, while leaving existing nodes alone.
      onSaved(entry, { idEntity: ids[draftId], reused: true }, ids);
      savedCount += 1;
      if (savedCount % 25 === 0) await new Promise(resolve => setTimeout(resolve, 0));
      return;
    }
    onStatus(`Saving ${kind}: ${entity.name || entity.summary || entity.title} (${savedCount + 1} of ${entries.length})...`);
    let record;
    if (!ids[draftId]) {
      if (kind === 'Place') {
        const parent = parents.get(draftId);
        const parentIdEntity = ids[parent] || (external.has(parent) ? parent : '');
        record = await api.savePlace({ sessionId: progress.sessionId, draftId, parentIdEntity,
          deferRelationships: true,
          recoverUnlinkedPlace: draftId === rootId,
          ...(draftId === rootId || entity.finalRecord ? { entity } : { suggestion: entity }),
          aogYear: 21000, prompt: draft.sourcePrompt || draft.prompt,
        });
      } else {
        let payload = entity;
        if (kind === 'Event') {
          payload = { ...entity, idPlace: ids[entity.placeDraftId] || null, details: JSON.stringify({
            description: entity.details,
            participants: (entity.participants || []).map(participant => ({
              ...participant, idEntity: ids[participant.entityDraftId],
            })),
            knownByEntityIds: (entity.knownByEntityIds || []).map(id => ids[id]),
          }) };
        }
        try {
          record = await api.saveEntity({ sessionId: progress.sessionId, kind, draftId, entity: payload });
        } catch (error) {
          throw new Error(`Could not save ${kind} "${entity.name || entity.summary || entity.title || draftId}": ${error?.message || error}`);
        }
      }
      if (!record?.idEntity) throw new Error(`No saved id returned for ${kind} ${entity.name || entity.summary}.`);
      ids[draftId] = record.idEntity;
      onProgress(snapshot());
    } else record = { ...entity, idEntity: ids[draftId], reused: true };
    savedCount += 1;
    onSaved(entry, record, { ...ids });
  };
  // Dependency order follows draft ids rather than generation order or names.
  const pendingPlaces = entries.filter(entry => entry.kind === 'Place');
  while (pendingPlaces.length) {
    if (shouldStop()) return stopped();
    const index = pendingPlaces.findIndex(entry => {
      const parent = parents.get(entry.draftId);
      return !parent || ids[parent] || external.has(parent);
    });
    if (index < 0) throw new Error('The sub-place hierarchy contains a cycle.');
    await save(pendingPlaces.splice(index, 1)[0]);
  }
  for (const entry of entries.filter(entry => entry.kind !== 'Place')) {
    if (shouldStop()) return stopped();
    await save(entry);
  }
  if (shouldStop()) return stopped();
  const holdings = catalog.filter(entry => Array.isArray(entry.entity.currencies) && entry.entity.currencies.length)
    .map(entry => ({ idEntity: ids[entry.draftId], currencies: entry.entity.currencies }));
  if (holdings.length && !currenciesSaved) {
    onStatus('Saving generated currency holdings...');
    for (let index = 0; index < holdings.length; index += 500) {
      if (shouldStop()) return stopped();
      await api.saveCurrencies({ holdings: holdings.slice(index, index + 500) });
    }
  }
  currenciesSaved = true;
  onProgress(snapshot());
  onStatus('Saving entity relationships...');
  const relationships = [...new Map(links.map(({ from, to, relateType, score, ...change }) => {
    const relation = { idEntity: ids[from] || from, idRelated: ids[to], relateType, ...(score !== undefined ? { score } : {}), ...change };
    return [`${relation.idEntity}|${relation.idRelated}|${relateType}`, relation];
  })).values()];
  for (let index = 0; index < relationships.length; index += 500) {
    if (shouldStop()) return stopped();
    await api.saveRelationships({ relationships: relationships.slice(index, index + 500) });
  }
  relationshipsSaved = true;
  onProgress(snapshot());
  const beings = catalog.filter(entry => entry.kind === 'Being');
  for (const [index, entry] of beings.entries()) {
    if (shouldStop()) return stopped();
    const idEntity = ids[entry.draftId];
    if (routinesSavedIds[entry.draftId] === idEntity) continue;
    onStatus(`Generating daily routines: ${entry.entity.name || entry.entity.entityName} (${index + 1} of ${beings.length})...`);
    let record;
    try {
      // Reuse a validated schedule if the response or browser checkpoint was lost.
      record = await api.generateRoutines({ idEntity, rootPlaceId: ids[rootId],
        placeIds: catalog.filter(candidate => candidate.kind === 'Place').map(candidate => ids[candidate.draftId]),
        forceRegenerate: false });
      if (record?.idEntity !== idEntity || !Array.isArray(record.routines) || !record.routines.length) {
        throw new Error('No saved daily routines returned.');
      }
    } catch (error) {
      throw new Error(`Could not generate routines for ${entry.entity.name || entry.entity.entityName || entry.draftId}: ${error?.message || error}`);
    }
    routinesSavedIds[entry.draftId] = idEntity;
    onProgress(snapshot());
    onRoutineSaved(entry, record, { ...ids });
  }
  if (!knowledgeSaved) {
    if (shouldStop()) return stopped();
    onStatus('Preparing historical tale and rumor dispersal after daily routines...');
    let job;
    if (knowledgeJobId) {
      try { job = await api.storyStatus({ jobId: knowledgeJobId }); }
      catch (error) { if (error.status !== 404) throw error; }
    } else {
      // Persist before requesting a proposal so a lost response reuses this job.
      knowledgeJobId = window.crypto?.randomUUID?.() || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g,
        character => { const value = Math.floor(Math.random() * 16); return (character === 'x' ? value : (value & 3) | 8).toString(16); });
      onProgress(snapshot());
    }
    if (!job) job = await api.planKnowledge({ jobId: knowledgeJobId, rootPlaceId: ids[rootId],
      currentDate: { age: 503, year: 1000, season: 'Oreth', day: 1 } });
    // A checkpoint may precede the original plan request.
    if (!job) throw new Error('Knowledge dispersal did not return a saved job.');
    while (!['ready', 'installed'].includes(job.status)) {
      if (shouldStop()) return stopped();
      onStatus(`Simulating historical tales and rumors: ${job.knowledge?.assessments?.length || 0} tellings assessed...`);
      job = await api.storyStep({ jobId: knowledgeJobId, revision: job.revision });
      if (job.status === 'failed') throw new Error(`Knowledge dispersal: ${job.error}`);
    }
    if (shouldStop()) return stopped();
    if (job.status !== 'installed') {
      onStatus(`Saving learning Events in ${job.target.database} (${job.target.profile})...`);
      try { job = await api.installStory({ jobId: knowledgeJobId, revision: job.revision, target: job.target }); }
      catch (error) {
        if (error.code === 'STORY_SOURCE_CHANGED') {
          knowledgeJobId = null; onProgress(snapshot());
          throw new Error('The saved tree changed during knowledge dispersal. Retry Place Tree Save to assess its current events and routines.');
        }
        throw error;
      }
    }
    if (job.status !== 'installed') throw new Error('Knowledge dispersal was not installed.');
    knowledgeSaved = true;
    onProgress(snapshot());
  }
  return { progress: snapshot(true), counts: Object.fromEntries(['Place', 'Being', 'Item', 'Event'].map(
    kind => [kind, entries.filter(entry => entry.kind === kind).length]
  )), existingIds };
};
